/**
 * Events API Service
 * High-level functions for interacting with the Bespokian Events API
 */

import { api, type Event, type Ticket, type EventsSearchResponse, type EventDetailResponse, type PaymentIntentResponse, type BookingResponse, type ETicketResponse, type Category, type CategoriesResponse } from '@/lib/api';

// ============================================
// Types
// ============================================

export interface SearchEventsParams {
  q?: string;
  category?: string;
  performer_id?: number;
  venue_id?: number;
  start_date?: string;
  end_date?: string;
  min_price?: number;
  max_price?: number;
  page?: number;
  per_page?: number;
}

export interface GetEventParams {
  min_price?: number;
  max_price?: number;
  instant_download_only?: boolean;
  eticket_only?: boolean;
}

export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
}

// ============================================
// Event Search & Discovery
// ============================================

/**
 * Search for events with various filters
 */
export async function searchEvents(params?: SearchEventsParams): Promise<EventsSearchResponse> {
  return api.events.search(params);
}

/**
 * Get event details with available tickets
 */
export async function getEventDetails(eventId: number, params?: GetEventParams): Promise<EventDetailResponse> {
  return api.events.getById(eventId, params);
}

// ============================================
// Payment & Booking
// ============================================

/**
 * Create a payment intent and lock tickets
 * Returns client_secret for Stripe and lock_id for booking
 */
export async function createPaymentIntent(
  ticketGroupId: number,
  quantity: number,
  eventId: number,
  customerEmail?: string
): Promise<PaymentIntentResponse> {
  return api.events.createPaymentIntent({
    ticket_group_id: ticketGroupId,
    quantity,
    event_id: eventId,
    customer_email: customerEmail,
  });
}

/**
 * Complete a booking after successful payment
 * Requires auth token
 */
export async function completeBooking(
  lockId: string,
  paymentIntentId: string,
  customer: CustomerInfo,
  deliveryMethod: string = 'E-Ticket',
  notes?: string
): Promise<BookingResponse> {
  return api.events.completeBooking({
    lock_id: lockId,
    payment_intent_id: paymentIntentId,
    delivery_method: deliveryMethod,
    customer,
    notes,
  });
}

// ============================================
// E-Tickets
// ============================================

/**
 * Retrieve e-tickets for a completed order
 * May return 404 if tickets are not yet available
 */
export async function getETickets(mercuryTransactionId: number): Promise<ETicketResponse> {
  return api.events.getETickets(mercuryTransactionId);
}

// ============================================
// Categories
// ============================================

export interface GetCategoriesParams {
  name?: string;
  parent_path?: string;
  depth?: number;
  page?: number;
  per_page?: number;
}

/**
 * Custom category groupings for user-friendly display
 * Maps API category names to logical groups independent of TicketNetwork's hierarchy
 */
const CATEGORY_GROUPS: { id: string; label: string; keywords: string[] }[] = [
  { 
    id: 'sports',
    label: 'Sports', 
    keywords: ['NBA', 'NFL', 'MLB', 'NHL', 'Soccer', 'Golf', 'Tennis'] 
  },
  { 
    id: 'shows',
    label: 'Shows', 
    keywords: ['Broadway', 'Comedy', 'Las Vegas'] 
  },
  { 
    id: 'festivals',
    label: 'Festivals', 
    keywords: ['Festivals'] 
  },
];

/**
 * Get event categories for filtering
 */
export async function getCategories(params?: GetCategoriesParams): Promise<CategoriesResponse> {
  return api.events.getCategories(params);
}

/**
 * Get hierarchical categories for display
 * Fetches popular categories from API and groups them into user-friendly buckets
 */
export async function getTopCategories(): Promise<Category[]> {
  // Fetch popular categories from API
  const topResponse = await api.events.getTopCategories();
  const apiCategories = topResponse.categories;

  // Group API categories into our custom structure
  const hierarchy: Category[] = CATEGORY_GROUPS.map(group => {
    // Find API categories that match this group's keywords
    const children = apiCategories.filter(cat => 
      group.keywords.some(keyword => 
        cat.name.toLowerCase().includes(keyword.toLowerCase())
      )
    );

    // Calculate total event count for the group
    const totalEventCount = children.reduce((sum, child) => sum + (child.eventCount || 0), 0);

    // Create a virtual parent category for the group
    return {
      path: `custom.${group.id}`, // Virtual path (not used for filtering)
      name: group.label,
      depth: 0,
      parentPath: '',
      eventCount: totalEventCount,
      children: children.length > 0 ? children : undefined,
    };
  });

  // Filter out empty groups and sort by event count
  return hierarchy
    .filter(group => group.children && group.children.length > 0)
    .sort((a, b) => (b.eventCount || 0) - (a.eventCount || 0));
}

// ============================================
// Utility Functions
// ============================================

/**
 * Convert API event to display-friendly format
 */
export function formatEventForDisplay(event: Event) {
  return {
    id: event.id,
    name: event.name,
    dateTime: `${event.date} ${event.time}`,
    venue: event.venue.name,
    location: [event.venue.city, event.venue.state].filter(Boolean).join(', '),
    category: event.category || 'Event',
    imageUrl: event.imageUrl,
    priceFromCents: event.priceRange ? Math.round(event.priceRange.min * 100) : null,
    ticketsAvailable: event.ticketsAvailable,
    performers: event.performers.map(p => p.name),
  };
}

/**
 * Convert API ticket to display-friendly format
 */
export function formatTicketForDisplay(ticket: Ticket) {
  return {
    id: ticket.id,
    section: ticket.section,
    row: ticket.row,
    seats: ticket.seats ? `${ticket.seats.low}-${ticket.seats.high}` : null,
    quantity: ticket.availableQuantity,
    purchasableQuantities: ticket.purchasableQuantities,
    priceCents: Math.round(ticket.price.wholesale * 100),
    faceValueCents: ticket.price.face ? Math.round(ticket.price.face * 100) : null,
    deliveryMethods: ticket.deliveryMethods,
    isInstantDelivery: ticket.isInstantDelivery,
    isETicket: ticket.isETicket,
    notes: ticket.notes,
  };
}

/**
 * Download e-ticket PDF from base64
 */
export function downloadETicketPdf(base64: string, filename: string = 'tickets.pdf') {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Re-export types for convenience
export type { Event, Ticket, EventsSearchResponse, EventDetailResponse, PaymentIntentResponse, BookingResponse, ETicketResponse, Category, CategoriesResponse };
